import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, MapPinned, PackageSearch, GraduationCap, Boxes } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { SERVICES, WORK_PRINCIPLES } from "../mock";
import { useSiteData } from "../context/SiteData";
import { findImage, resolveImage } from "../lib/api";

const PRINCIPLE_ICONS = [MapPinned, PackageSearch, GraduationCap];

const FALLBACK_SOFTWARE = [{
  id: "ems",
  kicker: "Staff & operations",
  name: "EMS — Employee Management System",
  features: [
    "Staff attendance & shift records across warehouses and retail outlets",
    "Salary and finance management for all employees",
    "Other employee records — contact details, role and tenure",
  ],
  mailSubject: "Software Access Request — EMS Employee Management System",
}];

export default function Services() {
  const { data, loaded } = useSiteData();
  const software = loaded && data.software.length ? data.software : FALLBACK_SOFTWARE;

  return (
    <>
      <PageHeader
        crumb="Services"
        title="Genuine parts, three authorised brands"
        intro="Every service runs on the same warehouse discipline — right part, right place, right time — whichever brand you need."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 space-y-20">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id}>
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={resolveImage(loaded ? findImage(data.images, "service-" + s.id)?.path : null, s.image)}
                  alt={s.title}
                  className="w-full h-[360px] object-cover"
                />
              </div>
              <div>
                <span className="font-mono-label text-xs inline-block border border-[var(--rs-border)] rounded-md px-3 py-1 text-[var(--rs-blue)]">{s.since}</span>
                <h2 className="font-head font-extrabold text-2xl md:text-4xl leading-tight mt-5">{s.title}</h2>
                <p className="text-[var(--rs-muted)] text-lg mt-4 leading-relaxed">{s.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-3 mt-7">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-center gap-2.5 text-sm text-[var(--rs-ink)]">
                      <span className="w-5 h-5 rounded-full bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center shrink-0"><Check size={12} strokeWidth={3} /></span>
                      {it}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-6 border-t border-[var(--rs-border)]">
                  <span className="font-mono-label text-xs text-[var(--rs-muted)] uppercase tracking-wider">{s.metaLabel}</span>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {s.meta.map((m) => (
                      <span key={m} className="chip inline-block bg-white border border-[var(--rs-border)] rounded-lg px-4 py-2 text-sm font-medium cursor-default">{m}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {/* Software */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Internal technology</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Software built and used in-house</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-3xl">Alongside its OEM distribution business, Rajiv Syndicate Group runs its own technology internally. Partner outlets and dealers can request access to the system below — the request goes directly to our team.</p></Reveal>
          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            {software.map((sw, i) => (
              <Reveal key={sw.id} delay={180 + i * 100}>
                <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-8 md:p-10 h-full flex flex-col">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[var(--rs-blue)] text-white flex items-center justify-center shrink-0"><Boxes size={26} /></div>
                    <div>
                      <span className="font-mono-label text-xs text-[var(--rs-muted)]">{sw.kicker}</span>
                      <h3 className="font-head font-bold text-xl">{sw.name}</h3>
                    </div>
                  </div>
                  {sw.description && <p className="text-[var(--rs-muted)] mt-5 leading-relaxed">{sw.description}</p>}
                  <ul className="mt-6 space-y-3">
                    {sw.features.map((it) => (
                      <li key={it} className="flex gap-3 text-sm text-[var(--rs-ink)]">
                        <Check size={16} className="text-[var(--rs-blue)] shrink-0 mt-0.5" strokeWidth={3} />{it}
                      </li>
                    ))}
                  </ul>
                  <a href={`mailto:rajiv.wh.ambala@gmail.com?subject=${encodeURIComponent(sw.mailSubject)}`} className="btn-primary rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2 self-start mt-7">Request Access <ArrowRight size={16} /></a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>How we work</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Right part, right place, right time</h2></Reveal>
        <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Every warehouse runs on modern inventory management practices, with staff regularly upskilled to keep retail outlets stocked without delay.</p></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {WORK_PRINCIPLES.map((p, i) => {
            const Icon = PRINCIPLE_ICONS[i];
            return (
              <Reveal key={p.title} delay={i * 110}>
                <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 h-full">
                  <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center"><Icon size={22} /></div>
                  <h3 className="font-head font-bold text-xl mt-5">{p.title}</h3>
                  <p className="text-[var(--rs-muted)] text-sm mt-3 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--rs-bg)] border-t border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
          <Reveal><h2 className="font-head font-extrabold text-3xl md:text-5xl">Need genuine parts for your outlet?</h2></Reveal>
          <Reveal delay={120}>
            <div className="flex flex-wrap gap-4 justify-center mt-9">
              <Link to="/contact" className="btn-primary rounded-lg px-7 py-3.5 text-sm font-semibold">Contact Us</Link>
              <Link to="/locations" className="btn-outline rounded-lg px-7 py-3.5 text-sm font-semibold">Find Your Nearest Warehouse</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
